import React from "react";
import { Layers, Copy as CopyIcon, Trash2, Maximize2, Wand2, Minimize2, Crosshair } from "lucide-react";
import ColorPicker, { pushRecentColor } from "../ColorPicker";
import { PropField, ToggleBtn } from "./Chrome";
import { editorInput, FONT_FAMILIES } from "./constants";

const PropertiesPanel = ({
  activeProps,
  onUpdate,
  onUpdateSize,
  onUpdateRadius,
  onDuplicate,
  onDelete,
  onBringToFront,
  onSendToBack,
  onFitToBleed,
  onFitInsideTrim,
  onCenter,
  onEditAI,
}) => {
  if (!activeProps) {
    return (
      <aside className="bg-white border-l border-[#D3D1C7] overflow-y-auto" data-testid="properties-panel">
        <div className="p-5 text-center text-xs text-[#5F5E5A]">
          <Layers className="w-6 h-6 mx-auto text-[#5F5E5A]/40 mb-3" strokeWidth={1.2} />
          Select a layer to edit its properties.
        </div>
      </aside>
    );
  }

  const isText = activeProps.type === "textbox" || activeProps.type === "text" || activeProps.type === "i-text";
  const isShape = ["rect", "circle", "line", "path", "triangle"].includes(activeProps.type);
  const isLine = activeProps.type === "line";
  const hasFill = activeProps.type !== "line";
  const hasW = ["rect", "image", "path", "textbox"].includes(activeProps.type);

  return (
    <aside className="bg-white border-l border-[#D3D1C7] overflow-y-auto" data-testid="properties-panel">
      <div className="p-4 space-y-4" data-testid="active-properties">
        <div className="flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.18em] text-[#5F5E5A]">{activeProps.type} properties</div>
          <button
            onClick={onDuplicate}
            data-testid="duplicate-btn"
            title="Duplicate (Ctrl+D)"
            className="p-1.5 rounded-md text-[#5F5E5A] hover:bg-[#F1EFE8] hover:text-[#534AB7]"
          >
            <CopyIcon className="w-3.5 h-3.5" />
          </button>
        </div>

        {isText && (
          <>
            <PropField label="Content">
              <textarea
                value={activeProps.text}
                onChange={(e) => onUpdate("text", e.target.value)}
                rows={3}
                data-testid="text-content-input"
                className={editorInput + " resize-none"}
              />
            </PropField>
            <PropField label="Font family">
              <select
                value={activeProps.fontFamily}
                onChange={(e) => onUpdate("fontFamily", e.target.value)}
                className={editorInput}
                data-testid="font-family-select"
              >
                {FONT_FAMILIES.map((f) => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
              </select>
            </PropField>
            <div className="grid grid-cols-2 gap-2">
              <PropField label="Size">
                <input type="number" value={activeProps.fontSize} onChange={(e) => onUpdate("fontSize", parseInt(e.target.value) || 16)} className={editorInput} />
              </PropField>
              <PropField label="Letter spacing">
                <input type="number" step="10" value={activeProps.charSpacing} onChange={(e) => onUpdate("charSpacing", parseInt(e.target.value) || 0)} className={editorInput} />
              </PropField>
            </div>
            <PropField label="Text colour">
              <ColorPicker value={activeProps.fill} onChange={(v) => { onUpdate("fill", v); pushRecentColor(v); }} testid="text-color-picker" />
            </PropField>
            <PropField label="Outline colour">
              <ColorPicker
                value={activeProps.stroke || "#000000"}
                onChange={(v) => { onUpdate("stroke", v); pushRecentColor(v); }}
                testid="text-stroke-picker"
              />
            </PropField>
            <PropField label="Outline width">
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="6"
                  step="0.5"
                  value={activeProps.strokeWidth || 0}
                  onChange={(e) => onUpdate("strokeWidth", parseFloat(e.target.value))}
                  data-testid="text-stroke-width"
                  className="flex-1"
                />
                <span className="text-xs tabular-nums w-8 text-right text-[#5F5E5A]">
                  {(activeProps.strokeWidth || 0).toFixed(1)}
                </span>
              </div>
            </PropField>
            {/* Text-block highlight / background colour for legibility on busy
                images. Empty string means no highlight. We use Fabric's
                `backgroundColor` (which fills the text frame as one solid
                block) plus a small `padding` for breathing room — the older
                `textBackgroundColor` only paints behind each line tightly,
                which looks raggedy over photo backgrounds. */}
            <PropField label="Highlight / background">
              <ColorPicker
                value={activeProps.backgroundColor || ""}
                onChange={(v) => {
                  onUpdate("backgroundColor", v);
                  // Give the text some breathing room when a background is
                  // applied — without padding the text touches the edges.
                  if (v && (!activeProps.padding || activeProps.padding < 6)) {
                    onUpdate("padding", 10);
                  }
                  if (v) pushRecentColor(v);
                }}
                allowEmpty
                testid="text-bg-picker"
              />
              {activeProps.backgroundColor && (
                <>
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-[#5F5E5A] mb-1">
                      <span>Padding</span>
                      <span className="tabular-nums">{activeProps.padding || 0}px</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="40"
                      step="1"
                      value={activeProps.padding || 0}
                      onChange={(e) => onUpdate("padding", parseInt(e.target.value) || 0)}
                      data-testid="text-bg-padding"
                      className="w-full"
                    />
                  </div>
                  <button
                    onClick={() => {
                      onUpdate("backgroundColor", "");
                      onUpdate("padding", 0);
                    }}
                    data-testid="text-bg-clear-btn"
                    className="mt-2 text-[11px] text-[#5F5E5A] underline hover:text-[#D74343]"
                  >
                    Remove highlight
                  </button>
                </>
              )}
            </PropField>
            <div className="flex gap-1">
              <ToggleBtn active={activeProps.fontWeight === "bold"} onClick={() => onUpdate("fontWeight", activeProps.fontWeight === "bold" ? "normal" : "bold")}>B</ToggleBtn>
              <ToggleBtn active={activeProps.fontStyle === "italic"} onClick={() => onUpdate("fontStyle", activeProps.fontStyle === "italic" ? "normal" : "italic")}><span className="italic">I</span></ToggleBtn>
              <ToggleBtn active={activeProps.underline} onClick={() => onUpdate("underline", !activeProps.underline)}><span className="underline">U</span></ToggleBtn>
            </div>
            <PropField label="Alignment">
              <div className="flex gap-1">
                {["left", "center", "right"].map((a) => (
                  <ToggleBtn key={a} active={activeProps.textAlign === a} onClick={() => onUpdate("textAlign", a)}>
                    {a[0].toUpperCase()}
                  </ToggleBtn>
                ))}
              </div>
            </PropField>
          </>
        )}

        {isShape && (
          <>
            {hasFill && (
              <PropField label="Fill">
                <ColorPicker value={activeProps.fill} onChange={(v) => { onUpdate("fill", v); pushRecentColor(v); }} testid="shape-fill-picker" />
              </PropField>
            )}
            <PropField label={isLine ? "Line colour" : "Stroke colour"}>
              <ColorPicker
                value={activeProps.stroke || (isLine ? "#534AB7" : "#000000")}
                onChange={(v) => { onUpdate("stroke", v); pushRecentColor(v); }}
                testid="shape-stroke-picker"
              />
            </PropField>
            <PropField label={isLine ? "Line thickness" : "Stroke width"}>
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="40"
                  value={activeProps.strokeWidth}
                  onChange={(e) => onUpdate("strokeWidth", parseInt(e.target.value))}
                  className="flex-1"
                />
                <span className="text-xs tabular-nums w-8 text-right text-[#5F5E5A]">{activeProps.strokeWidth}</span>
              </div>
            </PropField>

            {activeProps.type === "rect" && (
              <div className="grid grid-cols-2 gap-2">
                <PropField label="Width (px)">
                  <input type="number" min={1} value={activeProps.width} onChange={(e) => onUpdateSize(parseInt(e.target.value) || 1, null)} className={editorInput} data-testid="shape-width-input" />
                </PropField>
                <PropField label="Height (px)">
                  <input type="number" min={1} value={activeProps.height} onChange={(e) => onUpdateSize(null, parseInt(e.target.value) || 1)} className={editorInput} data-testid="shape-height-input" />
                </PropField>
              </div>
            )}
            {activeProps.type === "circle" && (
              <PropField label="Radius (px)">
                <input
                  type="number"
                  min={1}
                  value={Math.round((activeProps.radius || 0) * activeProps.scaleX)}
                  onChange={(e) => onUpdateRadius(parseInt(e.target.value) || 1)}
                  className={editorInput}
                  data-testid="circle-radius-input"
                />
              </PropField>
            )}
            {isLine && (
              <div className="grid grid-cols-2 gap-2">
                <PropField label="From X">
                  <input type="number" value={Math.round(activeProps.x1 || 0)} onChange={(e) => onUpdate("x1", parseInt(e.target.value) || 0)} className={editorInput} />
                </PropField>
                <PropField label="To X">
                  <input type="number" value={Math.round(activeProps.x2 || 0)} onChange={(e) => onUpdate("x2", parseInt(e.target.value) || 0)} className={editorInput} />
                </PropField>
                <PropField label="From Y">
                  <input type="number" value={Math.round(activeProps.y1 || 0)} onChange={(e) => onUpdate("y1", parseInt(e.target.value) || 0)} className={editorInput} />
                </PropField>
                <PropField label="To Y">
                  <input type="number" value={Math.round(activeProps.y2 || 0)} onChange={(e) => onUpdate("y2", parseInt(e.target.value) || 0)} className={editorInput} />
                </PropField>
              </div>
            )}
            {activeProps.type === "path" && (
              <div className="grid grid-cols-2 gap-2">
                <PropField label="Width (px)">
                  <input type="number" min={1} value={activeProps.width} onChange={(e) => onUpdateSize(parseInt(e.target.value) || 1, null)} className={editorInput} data-testid="path-width-input" />
                </PropField>
                <PropField label="Height (px)">
                  <input type="number" min={1} value={activeProps.height} onChange={(e) => onUpdateSize(null, parseInt(e.target.value) || 1)} className={editorInput} data-testid="path-height-input" />
                </PropField>
              </div>
            )}
          </>
        )}

        {!isShape && !isText && hasW && (
          <div className="grid grid-cols-2 gap-2">
            <PropField label="Width (px)">
              <input type="number" min={1} value={activeProps.width} onChange={(e) => onUpdateSize(parseInt(e.target.value) || 1, null)} className={editorInput} />
            </PropField>
            <PropField label="Height (px)">
              <input type="number" min={1} value={activeProps.height} onChange={(e) => onUpdateSize(null, parseInt(e.target.value) || 1)} className={editorInput} />
            </PropField>
          </div>
        )}

        {activeProps.type === "image" && onFitToBleed && (
          <div className="space-y-1.5">
            <button
              onClick={onFitToBleed}
              data-testid="fit-to-bleed-btn"
              className="w-full px-3 py-2 rounded-md bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489] flex items-center justify-center gap-2"
              title="Center the image and scale it to fill the entire bleed area (recommended for full-bleed cover images)"
            >
              <Maximize2 className="w-3 h-3" /> Center &amp; fit to bleed
            </button>
            {onFitInsideTrim && (
              <button
                onClick={onFitInsideTrim}
                data-testid="fit-inside-trim-btn"
                className="w-full px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8] flex items-center justify-center gap-2 text-[#2C2C2A]"
                title="Fit the image entirely inside the trim area (no edges cut off)"
              >
                <Minimize2 className="w-3 h-3" /> Fit inside trim (no crop)
              </button>
            )}
          </div>
        )}

        {onCenter && activeProps.type !== "image" && (
          <button
            onClick={onCenter}
            data-testid="center-on-canvas-btn"
            className="w-full px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8] flex items-center justify-center gap-2 text-[#534AB7]"
            title="Re-center this layer on the canvas"
          >
            <Crosshair className="w-3 h-3" /> Center on canvas
          </button>
        )}

        {activeProps.type === "image" && onEditAI && (
          <button
            onClick={onEditAI}
            data-testid="edit-with-ai-btn"
            className="w-full px-3 py-2 rounded-md bg-[#534AB7] text-white text-xs font-medium hover:bg-[#3C3489] flex items-center justify-center gap-2"
            title="Describe a change in plain English and AI will re-render the image"
          >
            <Wand2 className="w-3 h-3" /> Edit with AI
          </button>
        )}

        <PropField label="Opacity">
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={activeProps.opacity}
              onChange={(e) => onUpdate("opacity", parseFloat(e.target.value))}
              className="flex-1"
              data-testid="opacity-slider"
            />
            <span className="text-xs tabular-nums w-10 text-right text-[#5F5E5A]">
              {Math.round(activeProps.opacity * 100)}%
            </span>
          </div>
        </PropField>

        <div className="flex gap-1.5">
          <button onClick={onBringToFront} className="flex-1 px-2 py-1.5 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8]">
            Bring to front
          </button>
          <button onClick={onSendToBack} className="flex-1 px-2 py-1.5 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8]">
            Send to back
          </button>
        </div>
        <button
          onClick={onDuplicate}
          data-testid="duplicate-action-btn"
          className="w-full px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#F1EFE8] flex items-center justify-center gap-2 text-[#534AB7]"
        >
          <CopyIcon className="w-3 h-3" /> Duplicate (Ctrl+D)
        </button>
        <button
          onClick={onDelete}
          className="w-full px-3 py-2 rounded-md border border-[#D3D1C7] text-xs hover:bg-[#FFE9E9] hover:border-[#D74343] hover:text-[#D74343] flex items-center justify-center gap-2"
          data-testid="delete-active-btn"
        >
          <Trash2 className="w-3 h-3" /> Delete layer
        </button>
      </div>
    </aside>
  );
};

export default PropertiesPanel;
